import {
  DynamicField,
  DynamicForm,
  FieldVariant,
  SelectFieldProps,
  SelectOption,
  TextFieldProps,
} from '@/components/form-builder/form-builder.types';
import { MeetingRepository } from '@/store/meeting-state/meeting-state.repository';
import { User } from '@/types';
import { MeetingService } from '../../meeting.service';
import { BehaviorSubject } from 'rxjs';

interface IgetInvitationFormObjectProps {
  membersOptions?: User[];
  store: MeetingRepository;
  meetingService: MeetingService;
}

interface IgetExternalMembersFormObjectProps {
  store: MeetingRepository;
}

export const getAppMembersFormObject = ({ membersOptions, store, meetingService }: IgetInvitationFormObjectProps): DynamicForm => {
  const acOptions: SelectOption[] = [];
  const acFieldRerender = new BehaviorSubject<boolean>(true);

  const memberfieldSelect: DynamicField<SelectFieldProps> = {
    label: '',
    variant: FieldVariant.SELECT,
    isRequired: false,
    props: {
      placeholder: 'settings.committee.form.members.placeholder',
      options: membersOptions?.map(member => {
        return { name: member.email, code: member.id };
      }),
      value: store.getNestedObservable('dto', 'participants'),
      onSelectChange: (value: SelectOption) => {
        store.set('acSelected', { name: '', code: undefined });
        const member = membersOptions?.find(member => member.id === value.code);
        store.set('inviteeSelected', {
          nom: member?.firstName,
          prenom: member?.lastName,
          mail: member?.email,
          present: false,
          userId: member?.id,
        });
        if (member?.id) {
          meetingService.getSelectedUserAcs(member.id).subscribe(acs => {
            acOptions.length = 0;
            acOptions.push(
              ...acs.map(ac => {
                return { name: ac.nom, code: ac.id };
              })
            );
            if (acOptions.length > 0) {
              acFieldRerender.next(false);
            }
          });
        }
      },
    },
  };

  const acsField: DynamicField<SelectFieldProps> = {
    label: '',
    variant: FieldVariant.SELECT,
    isRequired: false,
    isDisabled: acFieldRerender,
    props: {
      value: store.getObservable('acSelected'),
      placeholder: 'meeting.invitation.sigmap.ac',
      options: acOptions,
      onSelectChange: (value: SelectOption) => {
        store.set('acSelected', value);
        store.setNested('inviteeSelected', 'organisme', value.name);
      },
    },
  };

  return {
    title: 'settings.members.add.title',
    description: '',
    isHeaderHidden: true,
    grids: [
      {
        title: '',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [memberfieldSelect, acsField],
          },
        ],
      },
    ],
  };
};

export const getInvitationFormObject = ({ store }: IgetExternalMembersFormObjectProps): DynamicForm => {
  const lastNameField: DynamicField<TextFieldProps> = {
    label: 'Nom',
    variant: FieldVariant.TEXT,
    error: store.getNestedObservableError('nom'),
    props: {
      value: store.getNestedObservable('inviteeSelected', 'nom'),
      onChange: (value: string) => {
        store.setNested('inviteeSelected', 'nom', value);
        store.clearError('nom');
      },
    },
    isRequired: true,
  };

  const firstNameField: DynamicField<TextFieldProps> = {
    label: 'Prénom',
    variant: FieldVariant.TEXT,
    error: store.getNestedObservableError('prenom'),
    props: {
      value: store.getNestedObservable('inviteeSelected', 'prenom'),
      onChange: (value: string) => {
        store.setNested('inviteeSelected', 'prenom', value);
        store.clearError('prenom');
      },
    },
    isRequired: true,
  };

  const emailField: DynamicField<TextFieldProps> = {
    label: 'Email',
    variant: FieldVariant.TEXT,
    error: store.getNestedObservableError('mail'),
    props: {
      value: store.getNestedObservable('inviteeSelected', 'mail'),
      onChange: (value: string) => {
        store.setNested('inviteeSelected', 'mail', value);
        store.clearError('mail');
      },
    },
    isRequired: true,
  };

  const functionField: DynamicField<TextFieldProps> = {
    label: 'Fonction',
    variant: FieldVariant.TEXT,
    error: store.getNestedObservableError('fonction'),
    isRequired: true,
    props: {
      value: store.getNestedObservable('inviteeSelected', 'fonction'),
      onChange: (value: string) => {
        store.setNested('inviteeSelected', 'fonction', value);
        store.clearError('fonction');
      },
    },
  };

  const organismField: DynamicField<TextFieldProps> = {
    label: 'Organisme',
    variant: FieldVariant.TEXT,
    isRequired: true,
    error: store.getNestedObservableError('organisme'),
    props: {
      value: store.getNestedObservable('inviteeSelected', 'organisme'),
      onChange: (value: string) => {
        store.setNested('inviteeSelected', 'organisme', value);
        store.clearError('organisme');
      },
    },
  };

  return {
    title: 'Invitation',
    description: '',
    orientation: 'horizontal',
    isHeaderHidden: true,
    grids: [
      {
        title: '',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [lastNameField, firstNameField],
          },
          {
            fields: [emailField, functionField],
          },
          {
            fields: [organismField],
          },
        ],
      },
    ],
  };
};
